import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { formatCurrency } from '../lib/utils';
import { SHARE_ROWS, pctOf, formatPct } from '../lib/costRevenueShare';

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const REGION_NAMES = { uk: 'UK', fr: 'France' };

function PctBar({ pct }) {
  const width = pct === null || pct === undefined ? 0 : Math.max(0, Math.min(100, pct));
  return (
    <div className="flex items-center gap-2 justify-end">
      <div className="h-2 w-24 rounded bg-muted overflow-hidden">
        <div
          className={`h-2 rounded ${pct > 100 ? 'bg-red-500' : 'bg-orange-400'}`}
          style={{ width: `${width}%` }}
        />
      </div>
      <span className="tabular-nums font-mono text-sm w-14 text-right">{formatPct(pct)}</span>
    </div>
  );
}

/**
 * Costs as a share of revenue (Booking Volume) for the Property Portfolio.
 * `share` comes from lib/costRevenueShare.computeCostRevenueShare.
 * `cv(value, fromCurrency)` converts a native amount to the display currency.
 */
export function CostRevenueShare({ share, displayCurrency, cv, year }) {
  const [showAllProps, setShowAllProps] = useState(false);
  if (!share || share.lastMonth === 0) return null;
  const { regions, months, propertyRows, lastMonth } = share;
  const uk = regions.uk;
  const fr = regions.fr;
  const fmt = (v, ccy) => formatCurrency(cv(v, ccy), displayCurrency);

  const totalRevenue = cv(uk.revenue, 'GBP') + cv(fr.revenue, 'EUR');
  const totalCosts = cv(uk.total, 'GBP') + cv(fr.total, 'EUR');
  const rowTotal = (key) => cv(uk.buckets[key], 'GBP') + cv(fr.buckets[key], 'EUR');
  const visibleRows = SHARE_ROWS.filter(
    (r) => r.key !== 'unclassified' || Math.abs(rowTotal(r.key)) > 0.005
  );
  const period = lastMonth === 1 ? `Jan ${year}` : `Jan to ${MONTHS_SHORT[lastMonth - 1]} ${year}`;
  const props = showAllProps ? propertyRows : propertyRows.slice(0, 10);

  const cellPct = 'text-right tabular-nums font-mono text-xs text-muted-foreground';
  const cellAmt = 'text-right tabular-nums font-mono text-sm';

  return (
    <div className="space-y-4" data-testid="cost-revenue-share">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Costs as % of revenue ({period})</CardTitle>
          <p className="text-xs text-muted-foreground">
            Revenue is Booking Volume (what guests paid), the same base as the dashboard.
            Cash view, same as the P&amp;L: mortgage repayments count in full.
          </p>
        </CardHeader>
        <CardContent className="pt-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead />
                <TableHead className="text-right">UK</TableHead>
                <TableHead className="text-right">% of rev.</TableHead>
                <TableHead className="text-right">France</TableHead>
                <TableHead className="text-right">% of rev.</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">% of rev.</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="bg-muted/40">
                <TableCell className="font-medium">Revenue (Booking Volume)</TableCell>
                <TableCell className={cellAmt}>{fmt(uk.revenue, 'GBP')}</TableCell>
                <TableCell className={cellPct}>100%</TableCell>
                <TableCell className={cellAmt}>{fmt(fr.revenue, 'EUR')}</TableCell>
                <TableCell className={cellPct}>100%</TableCell>
                <TableCell className={cellAmt}>{formatCurrency(totalRevenue, displayCurrency)}</TableCell>
                <TableCell className={cellPct}>100%</TableCell>
              </TableRow>
              {visibleRows.map((r) => (
                <TableRow key={r.key}>
                  <TableCell className="text-sm">{r.label}</TableCell>
                  <TableCell className={cellAmt}>{fmt(uk.buckets[r.key], 'GBP')}</TableCell>
                  <TableCell className={cellPct}>{formatPct(pctOf(uk.buckets[r.key], uk.revenue))}</TableCell>
                  <TableCell className={cellAmt}>{fmt(fr.buckets[r.key], 'EUR')}</TableCell>
                  <TableCell className={cellPct}>{formatPct(pctOf(fr.buckets[r.key], fr.revenue))}</TableCell>
                  <TableCell className={cellAmt}>{formatCurrency(rowTotal(r.key), displayCurrency)}</TableCell>
                  <TableCell className={cellPct}>{formatPct(pctOf(rowTotal(r.key), totalRevenue))}</TableCell>
                </TableRow>
              ))}
              <TableRow className="border-t-2 font-semibold">
                <TableCell>Total costs</TableCell>
                <TableCell className={cellAmt}>{fmt(uk.total, 'GBP')}</TableCell>
                <TableCell className="text-right tabular-nums font-mono text-sm">{formatPct(uk.pct)}</TableCell>
                <TableCell className={cellAmt}>{fmt(fr.total, 'EUR')}</TableCell>
                <TableCell className="text-right tabular-nums font-mono text-sm">{formatPct(fr.pct)}</TableCell>
                <TableCell className={cellAmt}>{formatCurrency(totalCosts, displayCurrency)}</TableCell>
                <TableCell className="text-right tabular-nums font-mono text-sm">{formatPct(pctOf(totalCosts, totalRevenue))}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="text-sm">Left after costs</TableCell>
                <TableCell className={cellAmt}>{fmt(uk.left, 'GBP')}</TableCell>
                <TableCell className={cellPct}>{formatPct(pctOf(uk.left, uk.revenue))}</TableCell>
                <TableCell className={cellAmt}>{fmt(fr.left, 'EUR')}</TableCell>
                <TableCell className={cellPct}>{formatPct(pctOf(fr.left, fr.revenue))}</TableCell>
                <TableCell className={cellAmt}>{formatCurrency(totalRevenue - totalCosts, displayCurrency)}</TableCell>
                <TableCell className={cellPct}>{formatPct(pctOf(totalRevenue - totalCosts, totalRevenue))}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Costs as % of revenue by month</CardTitle>
            <p className="text-xs text-muted-foreground">
              {MONTHS_SHORT[lastMonth - 1]} is still running, so its % will move.
            </p>
          </CardHeader>
          <CardContent className="pt-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">Costs</TableHead>
                  <TableHead className="text-right">UK</TableHead>
                  <TableHead className="text-right">France</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {months.map((m) => {
                  const rev = cv(m.uk.revenue, 'GBP') + cv(m.fr.revenue, 'EUR');
                  const cost = cv(m.uk.costs, 'GBP') + cv(m.fr.costs, 'EUR');
                  return (
                    <TableRow key={m.month}>
                      <TableCell className="text-sm">{MONTHS_SHORT[m.index]}</TableCell>
                      <TableCell className={cellAmt}>{formatCurrency(rev, displayCurrency)}</TableCell>
                      <TableCell className={cellAmt}>{formatCurrency(cost, displayCurrency)}</TableCell>
                      <TableCell className={cellPct}>{formatPct(m.uk.pct)}</TableCell>
                      <TableCell className={cellPct}>{formatPct(m.fr.pct)}</TableCell>
                      <TableCell><PctBar pct={pctOf(cost, rev)} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Costs as % of revenue by property</CardTitle>
            <p className="text-xs text-muted-foreground">
              Highest share first. Property costs only: costs booked to a company but not to one flat are in the totals, not here.
            </p>
          </CardHeader>
          <CardContent className="pt-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Property</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">Costs</TableHead>
                  <TableHead className="text-right">% of rev.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {props.map((p) => (
                  <TableRow key={p.property_id}>
                    <TableCell className="text-sm max-w-[220px] truncate" title={p.name}>
                      <span className="text-xs text-muted-foreground mr-1">{REGION_NAMES[p.region] || p.region}</span>
                      {p.name}
                    </TableCell>
                    <TableCell className={cellAmt}>{fmt(p.revenue, p.currency)}</TableCell>
                    <TableCell className={cellAmt}>{fmt(p.costs, p.currency)}</TableCell>
                    <TableCell><PctBar pct={p.pct} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {propertyRows.length > 10 && (
              <button
                type="button"
                className="mt-2 text-xs underline text-muted-foreground hover:text-foreground"
                onClick={() => setShowAllProps((v) => !v)}
              >
                {showAllProps ? 'Show top 10' : `Show all ${propertyRows.length}`}
              </button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
